import { StickerConfig } from "./sticker";
import { ModelConfig } from "./models";

/**
 * 尺寸计算器 - 将用户配置的尺寸转换为3D世界坐标
 */

export interface StickerSize3D {
  width: number; // 3D世界中的宽度
  height: number; // 3D世界中的高度
  radius: number; // 3D世界中的半径（圆形贴纸）
  scale: number; // 整体缩放比例
}

/**
 * 将用户单位（mm/cm）转换为统一的cm单位
 */
export function convertToRealSize(value: number, unit: "mm" | "cm"): number {
  return unit === "mm" ? value / 100 : value;
}

/**
 * 计算贴纸在3D世界中的实际尺寸
 * @param stickerConfig 贴纸配置
 * @param modelConfig 模型配置
 * @returns 3D世界中的贴纸尺寸
 */
export function calculateStickerSize3D(
  stickerConfig: StickerConfig,
  modelConfig: ModelConfig,
): StickerSize3D {
  // 将用户输入的尺寸转换为cm
  const realWidth = convertToRealSize(
    stickerConfig.size.width,
    stickerConfig.unit,
  );
  const realHeight = convertToRealSize(
    stickerConfig.size.height,
    stickerConfig.unit,
  );
  const realRadius = convertToRealSize(
    stickerConfig.size.radius,
    stickerConfig.unit,
  );

  // 应用模型的缩放比例
  const scale = modelConfig.realSize.scale;

  return {
    width: realWidth * scale,
    height: realHeight * scale,
    radius: realRadius * scale,
    scale: scale, // 整体缩放比例
  };
}

/**
 * 计算贴纸在纹理坐标中的尺寸（用于Texture模式）
 * @param stickerConfig 贴纸配置
 * @param modelConfig 模型配置
 * @param textureSize 纹理尺寸 (默认512x512)
 * @returns 纹理坐标中的尺寸
 */
export function calculateStickerSizeTexture(
  stickerConfig: StickerConfig,
  modelConfig: ModelConfig,
  textureSize: number = 512,
): { width: number; height: number; radius: number } {
  const size3D = calculateStickerSize3D(stickerConfig, modelConfig);

  // 将3D尺寸转换为纹理坐标尺寸
  // 这里需要根据模型的UV映射来调整比例
  const uvScale = 0.5; // 可以根据实际情况调整

  return {
    width: size3D.width * uvScale * textureSize,
    height: size3D.height * uvScale * textureSize,
    radius: size3D.radius * uvScale * textureSize,
  };
}

/**
 * 获取贴纸的显示尺寸信息（用于UI显示）
 * @param stickerConfig 贴纸配置
 * @param modelConfig 模型配置
 * @returns 显示信息
 */
export function getStickerDisplayInfo(
  stickerConfig: StickerConfig,
  modelConfig: ModelConfig,
): {
  realSize: { width: number; height: number; radius: number; unit: string };
  relativeSize: { width: number; height: number }; // 相对于模型的百分比
} {
  const realWidth = convertToRealSize(
    stickerConfig.size.width,
    stickerConfig.unit,
  );
  const realHeight = convertToRealSize(
    stickerConfig.size.height,
    stickerConfig.unit,
  );
  const realRadius = convertToRealSize(
    stickerConfig.size.radius,
    stickerConfig.unit,
  );

  const widthPercent = (realWidth / modelConfig.realSize.width) * 100;
  const heightPercent = (realHeight / modelConfig.realSize.height) * 100;

  return {
    realSize: {
      width: realWidth,
      height: realHeight,
      radius: realRadius,
      unit: "cm",
    },
    relativeSize: {
      width: Math.round(widthPercent),
      height: Math.round(heightPercent),
    },
  };
}
